/**
 * Centralized utility for resolving attachment URLs.
 *
 * Handles image URL inputs:
 * - Attachment IDs (UUIDs) - converted to signed S3 URLs
 * - Filenames (e.g., "image.png") - fetched from S3 by filename
 */

import { AttachmentStore } from "@/stores/attachment-store.core";
import { signedUrlCache } from "./signed-url-cache";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Determines if a string is a valid UUID (attachment ID format)
 */
export function isUuid(value: string): boolean {
   return UUID_REGEX.test(value);
}

/**
 * Gets the appropriate API endpoint for fetching a signed URL
 * based on whether the identifier is a UUID or legacy filename
 */
export function getSignedUrlEndpoint(identifier: string): string {
   if (isUuid(identifier)) {
      return `/api/v3/attachments/${identifier}/signed-url`;
   }
   // Filename (e.g., "image.png")
   return `/api/v3/attachments/filename/${identifier}/signed-url`;
}

export interface ResolveAttachmentUrlResult {
   url: string | null;
   fromCache: boolean;
}

export interface UploadAttachmentResult {
   attachmentId: string;
   signedUrl: string;
}

/**
 * Uploads a file to S3 and returns the attachment ID and signed URL.
 *
 * This consolidates the upload logic used by photo-uploader and logo-uploader.
 *
 * @param file - The file to upload
 * @returns Object containing the attachment ID and signed URL for display
 */
export async function uploadAttachment(file: File): Promise<UploadAttachmentResult> {
   const { name, size, type } = file;

   // Create attachment record
   const attachmentData = { name, mimetype: type, bytes: size };
   const response = await AttachmentStore.createAttachment(attachmentData).payload;
   const attachment = response.data;

   if (!attachment.id) {
      throw new Error("Failed to create attachment record");
   }

   // Upload file to S3
   const formData = new FormData();
   formData.append("upload", file);
   await AttachmentStore.uploadFile(attachment.id, formData).payload;

   // Get signed URL for display
   const signedUrlResponse = await AttachmentStore.getSignedUrl(
      `/api/v3/attachments/${attachment.id}/signed-url`,
   ).payload;
   const signedUrl = signedUrlResponse.data.url;

   if (!signedUrl) {
      throw new Error("Failed to get signed URL");
   }

   // Cache the signed URL
   signedUrlCache.set(attachment.id, signedUrl);

   return {
      attachmentId: attachment.id,
      signedUrl: signedUrl,
   };
}

/**
 * Resolves an attachment URL to a displayable signed S3 URL.
 *
 * - Checks cache first, then fetches signed URL from API
 *
 * @param identifier - UUID (attachment ID) or filename
 * @returns Object containing the resolved URL and whether it came from cache
 */
export async function resolveAttachmentUrl(
   identifier: string | null | undefined,
): Promise<ResolveAttachmentUrlResult> {
   if (!identifier) {
      return { url: null, fromCache: false };
   }

   // Check cache first
   const cachedUrl = signedUrlCache.get(identifier);
   if (cachedUrl) {
      return { url: cachedUrl, fromCache: true };
   }

   // Fetch signed URL from API
   try {
      const endpoint = getSignedUrlEndpoint(identifier);
      const response = await AttachmentStore.getSignedUrl(endpoint).payload;
      const signedUrl = response.data.url || null;

      if (signedUrl) {
         signedUrlCache.set(identifier, signedUrl);
      }

      return { url: signedUrl, fromCache: false };
   } catch (error) {
      console.error("Failed to resolve attachment URL:", error);
      return { url: null, fromCache: false };
   }
}

/**
 * Synchronously resolves an attachment URL from cache if available.
 * Returns null if async resolution would be required.
 *
 * Useful for initial render or when you want to avoid async operations.
 */
export function resolveAttachmentUrlSync(identifier: string | null | undefined): string | null {
   if (!identifier) {
      return null;
   }

   // Check cache
   return signedUrlCache.get(identifier);
}
